// FuelCharts component - Charts for fuel data visualization

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  formatDateShort,
  formatCurrency,
  formatNumber,
} from "../../utils/formatters";

const FuelCharts = ({ dailyChartData }) => {
  // Prepare chart data from API
  const chartData = dailyChartData
    .map((record) => ({
      date: record.date,
      dateShort: formatDateShort(record.date),
      amount: record.total_amount || 0,
      volume: record.total_volume || 0,
      count: record.fill_count || 0,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Custom tooltip for amount chart
  const AmountTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm font-medium text-gray-900">
            {payload[0].payload.date}
          </p>
          <p className="text-sm text-orange-600 font-semibold">
            จำนวนเงิน: {formatCurrency(payload[0].value)}
          </p>
          <p className="text-sm text-gray-600">
            เติม: {payload[0].payload.count} ครั้ง
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for volume chart
  const VolumeTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="text-sm font-medium text-gray-900">
            {payload[0].payload.date}
          </p>
          <p className="text-sm text-blue-600 font-semibold">
            ปริมาณ: {formatNumber(payload[0].value)} ลิตร
          </p>
        </div>
      );
    }
    return null;
  };

  if (!dailyChartData || dailyChartData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          กราฟข้อมูลน้ำมัน
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          <p>ไม่มีข้อมูลสำหรับแสดงกราฟ</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 mb-8">
      {/* Fuel Amount Timeline Chart */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          กราฟค่าน้ำมันรายวัน
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="dateShort"
                tick={{ fontSize: 12, fill: "#6B7280" }}
                stroke="#9CA3AF"
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6B7280" }}
                stroke="#9CA3AF"
                tickFormatter={(value) => formatNumber(value)}
              />
              <Tooltip content={<AmountTooltip />} />
              <Line
                type="monotone"
                dataKey="amount"
                stroke="#F97316"
                strokeWidth={3}
                dot={{ fill: "#F97316", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Fuel Volume Chart */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          กราฟปริมาณน้ำมันรายวัน
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="dateShort"
                tick={{ fontSize: 12, fill: "#6B7280" }}
                stroke="#9CA3AF"
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6B7280" }}
                stroke="#9CA3AF"
                tickFormatter={(value) => formatNumber(value)}
              />
              <Tooltip content={<VolumeTooltip />} />
              <Line
                type="monotone"
                dataKey="volume"
                stroke="#3B82F6"
                strokeWidth={3}
                dot={{ fill: "#3B82F6", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default FuelCharts;
